import { useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { ArrowDown, ArrowLeft, ArrowUp, Plus, Save, TriangleAlert, X } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Skeleton } from '@/components/ui/skeleton';
import { TooltipProvider } from '@/components/ui/tooltip';
import { PermissionButton } from '@/client/components/admin/PermissionButton';
import { FieldHint, FieldLabel, fieldClass } from '@/client/pages/Admin/shared/form-field';
import { useBreadcrumbs } from '@/client/hooks/use-breadcrumbs';
import { useGate } from '@/client/hooks/useGate';
import { getApiErrorMessage } from '@/client/utils/errors';
import {
  useAppsCatalogAppQuery,
  useAppsCatalogAppsQuery,
  useAppsCatalogMetaQuery,
  useAppsCatalogSectionsQuery,
} from '@/client/api/apps-catalog/apps-catalog.query';
import {
  getAppsCatalogFieldErrors,
  useCreateAppsCatalogAppMutation,
  useUpdateAppsCatalogAppMutation,
} from '@/client/api/apps-catalog/apps-catalog.mutation';
import type {
  AdminCatalogApp,
  AppCatalogAppFields,
  AppCatalogSite,
  AppCatalogStatus,
} from '@/client/types/apps-catalog';
import { AppStatusControl } from '@/client/components/admin/AppsCatalog/AppStatusControl';
import {
  APPS_CATALOG_PERMS,
  CatalogIcon,
  CatalogTile,
  STATUS_LABELS,
  StatusPill,
  formatCatalogDate,
  isRoutedKey,
  labelWindowState,
  parseCatalogDate,
  showCatalogWarnings,
  siteLabel,
  statusLabel,
  unroutedPublishWarning,
} from '@/client/components/admin/AppsCatalog/catalogUi';

// Limits mirror ff-api model/apps_catalog.py; the API stays the authority.
const KEY_RE = /^[a-z0-9]+(?:-[a-z0-9]+)*$/;
const KEY_MAX_LEN = 64;
const RESERVED_APP_KEYS = new Set(['catalog']);
const FEATURES_MAX = 20;
const FEATURE_MAX_LEN = 120;
const CUSTOM_KEY = '__custom__';
const DEFAULT_SITES: AppCatalogSite[] = ['marketplace', 'homes'];

const SELECT_CLASS = 'w-full rounded-md border border-gray-200 bg-white px-3 py-2 text-sm';
const TEXTAREA_CLASS =
  'w-full rounded-md border border-input bg-transparent px-3 py-2 text-sm shadow-sm placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring';

/** Every key the API may name in a 400 `fields` map that has an input here. */
const FIELD_KEYS = new Set([
  'app_key',
  'name',
  'description',
  'long_description',
  'section_key',
  'icon',
  'icon_url',
  'sites',
  'show_in_grid',
  'show_in_store',
  'marketing_label',
  'marketing_label_starts_at',
  'marketing_label_ends_at',
  'features',
  'price_label',
  'sort_order',
  'status',
]);

type FieldErrors = Record<string, string>;

interface FormState {
  appKeyChoice: string;
  customKey: string;
  name: string;
  description: string;
  long_description: string;
  section_key: string;
  icon: string;
  icon_url: string;
  price_label: string;
  sort_order: string;
  sites: AppCatalogSite[];
  sitesTouched: boolean;
  show_in_grid: boolean;
  show_in_store: boolean;
  marketing_label: string;
  starts_at: string;
  ends_at: string;
  features: string[];
  status: AppCatalogStatus;
}

const emptyForm = (): FormState => ({
  appKeyChoice: '',
  customKey: '',
  name: '',
  description: '',
  long_description: '',
  section_key: '',
  icon: '',
  icon_url: '',
  price_label: '',
  sort_order: '',
  sites: ['marketplace'],
  sitesTouched: false,
  show_in_grid: true,
  show_in_store: true,
  marketing_label: '',
  starts_at: '',
  ends_at: '',
  features: [],
  status: 'draft',
});

const pad = (n: number) => String(n).padStart(2, '0');

/** Stored UTC → a `datetime-local` value in the editor's own timezone. */
const toLocalInput = (value: string | null | undefined) => {
  const date = parseCatalogDate(value);
  if (!date) return '';
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}T${pad(
    date.getHours(),
  )}:${pad(date.getMinutes())}`;
};

/** A `datetime-local` value (local time) → ISO-8601 with Z, as the API expects. */
const fromLocalInput = (value: string): string | null => {
  if (!value) return null;
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? null : date.toISOString();
};

const appToForm = (app: AdminCatalogApp): FormState => ({
  ...emptyForm(),
  appKeyChoice: app.app_key,
  name: app.name ?? '',
  description: app.description ?? '',
  long_description: app.long_description ?? '',
  section_key: app.section_key ?? '',
  icon: app.icon ?? '',
  icon_url: app.icon_url ?? '',
  price_label: app.price_label ?? '',
  sort_order: app.sort_order != null ? String(app.sort_order) : '',
  sites: [...(app.sites ?? [])],
  sitesTouched: true,
  show_in_grid: app.show_in_grid,
  show_in_store: app.show_in_store,
  marketing_label: app.marketing_label ?? '',
  starts_at: toLocalInput(app.marketing_label_starts_at),
  ends_at: toLocalInput(app.marketing_label_ends_at),
  features: [...(app.features ?? [])],
  status: app.status,
});

const formToFields = (form: FormState, siteOrder: readonly string[]): AppCatalogAppFields => {
  const fields: AppCatalogAppFields = {
    name: form.name.trim(),
    description: form.description.trim(),
    long_description: form.long_description.trim() || null,
    section_key: form.section_key,
    icon: form.icon,
    icon_url: form.icon_url.trim() || null,
    sites: siteOrder.filter((site) => form.sites.includes(site as AppCatalogSite)) as AppCatalogSite[],
    show_in_grid: form.show_in_grid,
    show_in_store: form.show_in_store,
    marketing_label: form.marketing_label || null,
    marketing_label_starts_at: fromLocalInput(form.starts_at),
    marketing_label_ends_at: fromLocalInput(form.ends_at),
    features: form.features.map((f) => f.trim()).filter(Boolean),
    price_label: form.price_label.trim() || null,
  };
  if (form.sort_order.trim() !== '') fields.sort_order = Number(form.sort_order);
  return fields;
};

/** Only the fields that differ, so an edit's audit entry names what changed. */
const diffFields = (before: AppCatalogAppFields, after: AppCatalogAppFields): AppCatalogAppFields => {
  const changes: Record<string, unknown> = {};
  (Object.keys(after) as (keyof AppCatalogAppFields)[]).forEach((key) => {
    if (JSON.stringify(before[key]) !== JSON.stringify(after[key])) changes[key] = after[key];
  });
  return changes as AppCatalogAppFields;
};

const Card = ({ title, children, aside }: { title: string; children: ReactNode; aside?: ReactNode }) => (
  <section className="space-y-4 rounded-lg border bg-white p-5 shadow-sm">
    <div className="flex items-center justify-between gap-2">
      <h2 className="text-sm font-semibold text-gray-900">{title}</h2>
      {aside}
    </div>
    {children}
  </section>
);

const AppsCatalogAppEditorPage = () => {
  const { appKey } = useParams<{ appKey: string }>();
  const isEdit = !!appKey;
  const navigate = useNavigate();

  useBreadcrumbs([
    { label: 'Apps catalog', href: '/admin/apps-catalog' },
    { label: isEdit ? appKey! : 'New app' },
  ]);

  const { data: meta } = useAppsCatalogMetaQuery();
  const { data: sections = [], isLoading: sectionsLoading } = useAppsCatalogSectionsQuery();
  const { data: existingApps = [] } = useAppsCatalogAppsQuery({}, !isEdit);
  const appQuery = useAppsCatalogAppQuery(appKey, isEdit);
  const app = appQuery.data ?? null;

  const createMutation = useCreateAppsCatalogAppMutation();
  const updateMutation = useUpdateAppsCatalogAppMutation();
  const publishGate = useGate(APPS_CATALOG_PERMS.publish);
  const saving = createMutation.isPending || updateMutation.isPending;

  const [form, setForm] = useState<FormState>(emptyForm);
  const [errors, setErrors] = useState<FieldErrors>({});
  const loadedKey = useRef<string | null>(null);

  // Seed the form once per app (not on every refetch, which would clobber edits).
  useEffect(() => {
    if (!isEdit) {
      if (loadedKey.current !== null) {
        loadedKey.current = null;
        setForm(emptyForm());
        setErrors({});
      }
      return;
    }
    if (app && loadedKey.current !== app.app_key) {
      loadedKey.current = app.app_key;
      setForm(appToForm(app));
      setErrors({});
    }
  }, [isEdit, app]);

  const siteOrder = meta?.sites?.length ? meta.sites : DEFAULT_SITES;
  const statuses = meta?.statuses?.length
    ? meta.statuses
    : (Object.keys(STATUS_LABELS) as AppCatalogStatus[]);
  const takenKeys = useMemo(() => new Set(existingApps.map((a) => a.app_key)), [existingApps]);

  const effectiveKey = isEdit
    ? appKey!
    : form.appKeyChoice === CUSTOM_KEY
      ? form.customKey.trim()
      : form.appKeyChoice;
  const routed = isRoutedKey(meta, effectiveKey);

  const clearError = (key: string) =>
    setErrors((prev) => {
      if (!prev[key]) return prev;
      const next = { ...prev };
      delete next[key];
      return next;
    });

  const set = <K extends keyof FormState>(key: K, value: FormState[K], errorKey: string = key) => {
    setForm((prev) => ({ ...prev, [key]: value }));
    clearError(errorKey);
  };

  const chooseSection = (sectionKey: string) => {
    setForm((prev) => ({
      ...prev,
      section_key: sectionKey,
      // Match the API's default until the editor picks sites themselves:
      // Homes apps show on both sites, the rest on marketplace.
      sites: prev.sitesTouched
        ? prev.sites
        : sectionKey === 'homes'
          ? ['marketplace', 'homes']
          : ['marketplace'],
    }));
    clearError('section_key');
  };

  const toggleSite = (site: AppCatalogSite, on: boolean) => {
    setForm((prev) => ({
      ...prev,
      sitesTouched: true,
      sites: on ? [...prev.sites.filter((s) => s !== site), site] : prev.sites.filter((s) => s !== site),
    }));
    clearError('sites');
  };

  const setFeature = (index: number, value: string) => {
    setForm((prev) => ({ ...prev, features: prev.features.map((f, i) => (i === index ? value : f)) }));
    clearError('features');
  };

  const moveFeature = (index: number, dir: -1 | 1) =>
    setForm((prev) => {
      const other = index + dir;
      if (other < 0 || other >= prev.features.length) return prev;
      const features = [...prev.features];
      [features[index], features[other]] = [features[other], features[index]];
      return { ...prev, features };
    });

  const removeFeature = (index: number) => {
    setForm((prev) => ({ ...prev, features: prev.features.filter((_, i) => i !== index) }));
    clearError('features');
  };

  const validate = (): FieldErrors => {
    const e: FieldErrors = {};
    if (!isEdit) {
      if (!form.appKeyChoice) e.app_key = 'required';
      else if (form.appKeyChoice === CUSTOM_KEY) {
        const key = form.customKey.trim();
        if (!key) e.app_key = 'required';
        else if (key.length > KEY_MAX_LEN || !KEY_RE.test(key))
          e.app_key = `lowercase letters and digits joined by single hyphens, at most ${KEY_MAX_LEN} characters`;
        else if (RESERVED_APP_KEYS.has(key)) e.app_key = 'reserved';
      }
      if (effectiveKey && takenKeys.has(effectiveKey)) e.app_key = 'already in the catalog';
    }
    if (!form.name.trim()) e.name = 'required';
    if (!form.description.trim()) e.description = 'required';
    if (!form.section_key) e.section_key = 'required';
    if (!form.icon) e.icon = 'required';
    if (form.icon_url.trim() && !form.icon_url.trim().startsWith('https://'))
      e.icon_url = 'an https:// URL';
    if (form.sites.length === 0) e.sites = 'pick at least one site';
    if (form.sort_order.trim() !== '') {
      const n = Number(form.sort_order);
      if (!Number.isInteger(n) || n < 0) e.sort_order = 'a whole number, 0 or more';
    }
    const start = fromLocalInput(form.starts_at);
    const end = fromLocalInput(form.ends_at);
    if (start && end && end <= start) e.marketing_label_ends_at = 'must be after the start';
    const features = form.features.map((f) => f.trim()).filter(Boolean);
    if (features.length > FEATURES_MAX) e.features = `at most ${FEATURES_MAX} features`;
    else if (features.some((f) => f.length > FEATURE_MAX_LEN))
      e.features = `each feature at most ${FEATURE_MAX_LEN} characters`;
    return e;
  };

  const handleSubmit = async () => {
    const clientErrors = validate();
    if (Object.keys(clientErrors).length > 0) {
      setErrors(clientErrors);
      toast.error('Fix the highlighted fields');
      return;
    }
    const fields = formToFields(form, siteOrder);
    try {
      if (isEdit && app) {
        // Never send app_key or status here: the API refuses both on edit.
        const changes = diffFields(formToFields(appToForm(app), siteOrder), fields);
        if (Object.keys(changes).length === 0) {
          toast.info('No changes to save');
          return;
        }
        const updated = await updateMutation.mutateAsync({ app_key: app.app_key, changes });
        if (updated) setForm(appToForm(updated));
        setErrors({});
        toast.success('App saved');
      } else {
        const result = await createMutation.mutateAsync({
          ...fields,
          app_key: effectiveKey,
          name: fields.name ?? '',
          description: fields.description ?? '',
          section_key: fields.section_key ?? '',
          icon: fields.icon ?? '',
          status: form.status,
        });
        const createdKey = result.app?.app_key ?? effectiveKey;
        toast.success(`${result.app?.name ?? createdKey} created as ${statusLabel(form.status)}`);
        showCatalogWarnings(result.warnings);
        navigate(`/admin/apps-catalog/apps/${encodeURIComponent(createdKey)}`, { replace: true });
      }
    } catch (error) {
      setErrors(getAppsCatalogFieldErrors(error));
      toast.error(isEdit ? 'Save failed' : 'Create failed', {
        description: getApiErrorMessage(error, 'Something went wrong.'),
      });
    }
  };

  const generalErrors = Object.entries(errors).filter(([key]) => !FIELD_KEYS.has(key));
  const labelState = app ? labelWindowState(app) : 'none';
  const timeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;

  if (isEdit && appQuery.isLoading) {
    return (
      <div className="min-h-screen space-y-4 bg-gray-50 p-6">
        <Skeleton className="h-8 w-64" />
        <Skeleton className="h-64 w-full" />
        <Skeleton className="h-64 w-full" />
      </div>
    );
  }

  if (isEdit && !app) {
    return (
      <div className="min-h-screen bg-gray-50 p-6">
        <div className="mx-auto max-w-lg rounded-lg border bg-white p-8 text-center shadow-sm">
          <p className="text-sm text-gray-700">
            {(appQuery.error as any)?.status === 404
              ? `There is no app with the key ${appKey}.`
              : getApiErrorMessage(appQuery.error, 'Could not load this app.')}
          </p>
          <Button variant="outline" className="mt-4" asChild>
            <Link to="/admin/apps-catalog">Back to apps</Link>
          </Button>
        </div>
      </div>
    );
  }

  return (
    <TooltipProvider delayDuration={200}>
      <div className="min-h-screen bg-gray-50 p-6">
        <div className="mx-auto max-w-screen-xl space-y-6">
          <div className="flex flex-wrap items-start justify-between gap-4">
            <div>
              <Link
                to="/admin/apps-catalog"
                className="mb-2 inline-flex items-center gap-1 text-xs font-medium text-gray-500 hover:text-gray-800"
              >
                <ArrowLeft className="h-3.5 w-3.5" />
                Apps catalog
              </Link>
              <h1 className="flex items-center gap-2 text-2xl font-bold text-gray-900">
                {isEdit ? `Edit ${app?.name ?? appKey}` : 'New app'}
                {app ? <StatusPill status={app.status} /> : null}
              </h1>
              {isEdit && <p className="mt-1 font-mono text-xs text-gray-500">{appKey}</p>}
            </div>
            <div className="flex flex-wrap items-center gap-2">
              {app && <AppStatusControl app={app} meta={meta} size="default" />}
              <PermissionButton
                code={APPS_CATALOG_PERMS.write}
                className="gap-1.5"
                disabled={saving}
                onClick={() => void handleSubmit()}
              >
                <Save className="h-4 w-4" />
                {saving ? 'Saving…' : isEdit ? 'Save changes' : 'Create app'}
              </PermissionButton>
            </div>
          </div>

          {generalErrors.length > 0 && (
            <div className="rounded-md border border-red-200 bg-red-50 p-3 text-sm text-red-700">
              {generalErrors.map(([key, reason]) => (
                <p key={key}>
                  <span className="font-medium">{key}</span>: {reason}
                </p>
              ))}
            </div>
          )}

          <div className="grid gap-6 lg:grid-cols-[minmax(0,1fr)_320px]">
            <div className="space-y-6">
              <Card title="Identity">
                <div className="space-y-1.5">
                  <FieldLabel
                    required={!isEdit}
                    tip="ff-ui maps the key to a route. Pick a known key; a custom key is listed but cannot open until ff-ui ships a route for it."
                  >
                    App key
                  </FieldLabel>
                  {isEdit ? (
                    <Input value={appKey} readOnly disabled className="font-mono" />
                  ) : (
                    <>
                      <select
                        className={fieldClass(SELECT_CLASS, errors.app_key)}
                        value={form.appKeyChoice}
                        onChange={(e) => set('appKeyChoice', e.target.value, 'app_key')}
                        aria-invalid={!!errors.app_key}
                      >
                        <option value="">Choose a key…</option>
                        {(meta?.known_app_keys ?? []).map(({ app_key, routed: isRouted }) => {
                          const taken = takenKeys.has(app_key);
                          return (
                            <option key={app_key} value={app_key} disabled={taken}>
                              {app_key}
                              {isRouted ? '' : ' (no ff-ui route yet)'}
                              {taken ? ' (already in catalog)' : ''}
                            </option>
                          );
                        })}
                        <option value={CUSTOM_KEY}>Custom (coming soon)</option>
                      </select>
                      {form.appKeyChoice === CUSTOM_KEY && (
                        <Input
                          className={fieldClass('font-mono', errors.app_key)}
                          placeholder="my-new-app"
                          value={form.customKey}
                          maxLength={KEY_MAX_LEN}
                          onChange={(e) => set('customKey', e.target.value.toLowerCase(), 'app_key')}
                          aria-invalid={!!errors.app_key}
                        />
                      )}
                    </>
                  )}
                  <FieldHint error={errors.app_key} />
                  {effectiveKey && meta && !routed && !errors.app_key && (
                    <p className="text-[11px] text-amber-700">
                      ff-ui has no route for this key yet. Keep it Coming soon until it does.
                    </p>
                  )}
                  {isEdit && (
                    <p className="text-[11px] text-gray-500">The key cannot change after create.</p>
                  )}
                </div>

                <div className="space-y-1.5">
                  <FieldLabel required>Name</FieldLabel>
                  <Input
                    className={fieldClass('', errors.name)}
                    value={form.name}
                    maxLength={150}
                    placeholder="Promotions Manager"
                    onChange={(e) => set('name', e.target.value)}
                    aria-invalid={!!errors.name}
                  />
                  <FieldHint error={errors.name} />
                </div>

                <div className="space-y-1.5">
                  <FieldLabel required tip="One line: the grid subtitle and the store card copy.">
                    Description
                  </FieldLabel>
                  <Input
                    className={fieldClass('', errors.description)}
                    value={form.description}
                    maxLength={255}
                    placeholder="Manage ads"
                    onChange={(e) => set('description', e.target.value.replace(/[\r\n]+/g, ' '))}
                    aria-invalid={!!errors.description}
                  />
                  <FieldHint error={errors.description} />
                </div>

                <div className="space-y-1.5">
                  <FieldLabel tip="Longer copy for the store drawer.">Long description</FieldLabel>
                  <textarea
                    className={fieldClass(TEXTAREA_CLASS, errors.long_description)}
                    rows={5}
                    maxLength={5000}
                    value={form.long_description}
                    onChange={(e) => set('long_description', e.target.value)}
                    aria-invalid={!!errors.long_description}
                  />
                  <FieldHint error={errors.long_description} />
                </div>
              </Card>

              <Card title="Placement">
                <div className="grid gap-4 sm:grid-cols-2">
                  <div className="space-y-1.5">
                    <FieldLabel required tip="Changing the section moves the app.">
                      Section
                    </FieldLabel>
                    {sectionsLoading ? (
                      <Skeleton className="h-9 w-full" />
                    ) : (
                      <select
                        className={fieldClass(SELECT_CLASS, errors.section_key)}
                        value={form.section_key}
                        onChange={(e) => chooseSection(e.target.value)}
                        aria-invalid={!!errors.section_key}
                      >
                        <option value="">Choose a section…</option>
                        {sections.map((section) => (
                          <option key={section.key} value={section.key}>
                            {section.label}
                            {section.visible ? '' : ' (hidden)'}
                          </option>
                        ))}
                      </select>
                    )}
                    <FieldHint error={errors.section_key} />
                  </div>

                  <div className="space-y-1.5">
                    <FieldLabel tip="Position within the section. Leave blank on create to add it last.">
                      Sort order
                    </FieldLabel>
                    <Input
                      type="number"
                      min={0}
                      step={1}
                      className={fieldClass('', errors.sort_order)}
                      value={form.sort_order}
                      placeholder={isEdit ? '' : 'Last in section'}
                      onChange={(e) => set('sort_order', e.target.value)}
                      aria-invalid={!!errors.sort_order}
                    />
                    <FieldHint error={errors.sort_order} />
                  </div>
                </div>

                <div className="space-y-1.5">
                  <FieldLabel required>Sites</FieldLabel>
                  <div className="flex flex-wrap gap-4">
                    {siteOrder.map((site) => (
                      <label key={site} className="flex items-center gap-2 text-sm text-gray-700">
                        <input
                          type="checkbox"
                          className="rounded border-gray-300"
                          checked={form.sites.includes(site)}
                          onChange={(e) => toggleSite(site, e.target.checked)}
                        />
                        {siteLabel(site)}
                      </label>
                    ))}
                  </div>
                  <FieldHint error={errors.sites} />
                </div>

                <div className="space-y-1.5">
                  <FieldLabel>Surfaces</FieldLabel>
                  <div className="flex flex-wrap gap-4">
                    <label className="flex items-center gap-2 text-sm text-gray-700">
                      <input
                        type="checkbox"
                        className="rounded border-gray-300"
                        checked={form.show_in_grid}
                        onChange={(e) => set('show_in_grid', e.target.checked)}
                      />
                      Header Apps grid
                    </label>
                    <label className="flex items-center gap-2 text-sm text-gray-700">
                      <input
                        type="checkbox"
                        className="rounded border-gray-300"
                        checked={form.show_in_store}
                        onChange={(e) => set('show_in_store', e.target.checked)}
                      />
                      App store (/app/store)
                    </label>
                  </div>
                  <FieldHint error={errors.show_in_grid ?? errors.show_in_store} />
                </div>
              </Card>

              <Card title="Appearance">
                <div className="grid gap-4 sm:grid-cols-2">
                  <div className="space-y-1.5">
                    <FieldLabel required tip="Lucide icon name, from the API's closed list.">
                      Icon
                    </FieldLabel>
                    <div className="flex items-center gap-2">
                      <span className="flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-md border bg-gray-50 text-gray-700">
                        {form.icon ? <CatalogIcon name={form.icon} className="h-4 w-4" /> : null}
                      </span>
                      <select
                        className={fieldClass(SELECT_CLASS, errors.icon)}
                        value={form.icon}
                        onChange={(e) => set('icon', e.target.value)}
                        aria-invalid={!!errors.icon}
                      >
                        <option value="">Choose an icon…</option>
                        {(meta?.icons ?? []).map((icon) => (
                          <option key={icon} value={icon}>
                            {icon}
                          </option>
                        ))}
                      </select>
                    </div>
                    <FieldHint error={errors.icon} />
                  </div>

                  <div className="space-y-1.5">
                    <FieldLabel tip="Optional image that replaces the icon. Must be https.">
                      Icon URL
                    </FieldLabel>
                    <Input
                      className={fieldClass('', errors.icon_url)}
                      value={form.icon_url}
                      maxLength={1024}
                      placeholder="https://…"
                      onChange={(e) => set('icon_url', e.target.value)}
                      aria-invalid={!!errors.icon_url}
                    />
                    <FieldHint error={errors.icon_url} />
                  </div>

                  <div className="space-y-1.5">
                    <FieldLabel tip="Display only, e.g. Free. Not a SKU.">Price label</FieldLabel>
                    <Input
                      className={fieldClass('', errors.price_label)}
                      value={form.price_label}
                      maxLength={50}
                      placeholder="Free"
                      onChange={(e) => set('price_label', e.target.value)}
                      aria-invalid={!!errors.price_label}
                    />
                    <FieldHint error={errors.price_label} />
                  </div>
                </div>

                <div className="space-y-3 border-t pt-4">
                  <div className="flex flex-wrap items-center justify-between gap-2">
                    <FieldLabel tip="The chip on the tile. It shows only inside its date window; either end may be left open.">
                      Marketing label
                    </FieldLabel>
                    {app && labelState !== 'none' && (
                      <span
                        className={`text-xs font-medium ${
                          labelState === 'live'
                            ? 'text-green-700'
                            : labelState === 'scheduled'
                              ? 'text-amber-700'
                              : 'text-gray-500'
                        }`}
                      >
                        Saved label is{' '}
                        {labelState === 'live' ? 'live now' : labelState === 'scheduled' ? 'scheduled' : 'not showing'}
                      </span>
                    )}
                  </div>
                  <div className="grid gap-4 sm:grid-cols-3">
                    <div className="space-y-1.5">
                      <span className="text-[11px] text-gray-500">Label</span>
                      <select
                        className={fieldClass(SELECT_CLASS, errors.marketing_label)}
                        value={form.marketing_label}
                        onChange={(e) => set('marketing_label', e.target.value)}
                        aria-invalid={!!errors.marketing_label}
                      >
                        <option value="">None</option>
                        {(meta?.marketing_labels ?? []).map((label) => (
                          <option key={label} value={label}>
                            {label}
                          </option>
                        ))}
                      </select>
                      <FieldHint error={errors.marketing_label} />
                    </div>
                    <div className="space-y-1.5">
                      <span className="text-[11px] text-gray-500">Starts</span>
                      <Input
                        type="datetime-local"
                        className={fieldClass('', errors.marketing_label_starts_at)}
                        value={form.starts_at}
                        onChange={(e) => set('starts_at', e.target.value, 'marketing_label_starts_at')}
                        aria-invalid={!!errors.marketing_label_starts_at}
                      />
                      <FieldHint error={errors.marketing_label_starts_at} />
                    </div>
                    <div className="space-y-1.5">
                      <span className="text-[11px] text-gray-500">Ends</span>
                      <Input
                        type="datetime-local"
                        className={fieldClass('', errors.marketing_label_ends_at)}
                        value={form.ends_at}
                        onChange={(e) => set('ends_at', e.target.value, 'marketing_label_ends_at')}
                        aria-invalid={!!errors.marketing_label_ends_at}
                      />
                      <FieldHint error={errors.marketing_label_ends_at} />
                    </div>
                  </div>
                  <p className="text-[11px] text-gray-500">
                    Times are in your timezone ({timeZone}) and saved as UTC.
                  </p>
                </div>
              </Card>

              <Card
                title="Features"
                aside={
                  <Button
                    size="sm"
                    variant="outline"
                    className="gap-1.5"
                    disabled={form.features.length >= FEATURES_MAX}
                    onClick={() => set('features', [...form.features, ''])}
                  >
                    <Plus className="h-3.5 w-3.5" />
                    Add feature
                  </Button>
                }
              >
                <p className="text-xs text-gray-500">
                  Store bullets, in order. Up to {FEATURES_MAX}, each at most {FEATURE_MAX_LEN} characters.
                  Blank rows are dropped on save.
                </p>
                {form.features.length === 0 ? (
                  <p className="text-sm text-gray-400">No features.</p>
                ) : (
                  <ol className="space-y-2">
                    {form.features.map((feature, index) => (
                      <li key={index} className="flex items-center gap-1.5">
                        <span className="w-5 text-right text-xs tabular-nums text-gray-400">{index + 1}</span>
                        <Input
                          value={feature}
                          maxLength={FEATURE_MAX_LEN}
                          onChange={(e) => setFeature(index, e.target.value)}
                          placeholder="Campaign creation"
                        />
                        <Button
                          size="sm"
                          variant="ghost"
                          className="h-8 w-8 p-0"
                          disabled={index === 0}
                          onClick={() => moveFeature(index, -1)}
                          title="Move up"
                        >
                          <ArrowUp className="h-3.5 w-3.5" />
                        </Button>
                        <Button
                          size="sm"
                          variant="ghost"
                          className="h-8 w-8 p-0"
                          disabled={index >= form.features.length - 1}
                          onClick={() => moveFeature(index, 1)}
                          title="Move down"
                        >
                          <ArrowDown className="h-3.5 w-3.5" />
                        </Button>
                        <Button
                          size="sm"
                          variant="ghost"
                          className="h-8 w-8 p-0 text-red-600"
                          onClick={() => removeFeature(index)}
                          title="Remove"
                        >
                          <X className="h-3.5 w-3.5" />
                        </Button>
                      </li>
                    ))}
                  </ol>
                )}
                <FieldHint error={errors.features} />
              </Card>
            </div>

            <aside className="space-y-6">
              <Card title="Status">
                {isEdit && app ? (
                  <div className="space-y-3 text-sm">
                    <div className="flex items-center justify-between gap-2">
                      <StatusPill status={app.status} />
                      <AppStatusControl app={app} meta={meta} />
                    </div>
                    <p className="text-xs text-gray-500">
                      Status changes separately from Save and asks for confirmation.
                    </p>
                    <dl className="grid grid-cols-[auto_1fr] gap-x-3 gap-y-1 text-xs">
                      <dt className="text-gray-500">Published</dt>
                      <dd className="text-gray-800">{formatCatalogDate(app.date_published)}</dd>
                      <dt className="text-gray-500">Created</dt>
                      <dd className="text-gray-800">{formatCatalogDate(app.date_added)}</dd>
                      <dt className="text-gray-500">Modified</dt>
                      <dd className="text-gray-800">{formatCatalogDate(app.date_modified)}</dd>
                    </dl>
                  </div>
                ) : (
                  <div className="space-y-1.5">
                    <FieldLabel tip="Anything other than Draft needs the publish permission.">
                      Initial status
                    </FieldLabel>
                    <select
                      className={fieldClass(SELECT_CLASS, errors.status)}
                      value={form.status}
                      onChange={(e) => set('status', e.target.value as AppCatalogStatus)}
                    >
                      {statuses.map((status) => (
                        <option
                          key={status}
                          value={status}
                          disabled={status !== 'draft' && !publishGate.allowed}
                        >
                          {statusLabel(status)}
                        </option>
                      ))}
                    </select>
                    <FieldHint error={errors.status} />
                    {!publishGate.allowed && (
                      <p className="text-[11px] text-gray-500">{publishGate.reason} to create as anything but Draft.</p>
                    )}
                    {form.status === 'published' && effectiveKey && !routed && (
                      <div className="flex gap-2 rounded-md border border-amber-200 bg-amber-50 p-2.5 text-xs text-amber-900">
                        <TriangleAlert className="mt-0.5 h-3.5 w-3.5 flex-shrink-0 text-amber-600" />
                        <p>{unroutedPublishWarning(effectiveKey)}</p>
                      </div>
                    )}
                  </div>
                )}
              </Card>

              <Card title="Tile">
                <CatalogTile
                  showFlags
                  app={{
                    name: form.name,
                    description: form.description,
                    icon: form.icon,
                    icon_url: form.icon_url.trim().startsWith('https://') ? form.icon_url.trim() : null,
                    marketing_label: form.marketing_label || null,
                    status: isEdit && app ? app.status : form.status,
                    price_label: form.price_label || null,
                    show_in_grid: form.show_in_grid,
                    show_in_store: form.show_in_store,
                  }}
                />
                <p className="text-[11px] text-gray-500">
                  Rough preview. The chip shows here whatever its date window; the Preview tab applies
                  the public rules.
                </p>
              </Card>
            </aside>
          </div>
        </div>
      </div>
    </TooltipProvider>
  );
};

export default AppsCatalogAppEditorPage;
